import React, { useEffect, useState } from "react";
import { Toaster } from "sonner";
import TopNav from "@/components/dashboard/TopNav";
import HeroImpact from "@/components/dashboard/HeroImpact";
import CommunityTracker from "@/components/dashboard/CommunityTracker";
import MilestonesTracker from "@/components/dashboard/MilestonesTracker";
import ImpactAllocation from "@/components/dashboard/ImpactAllocation";
import CauseSelection from "@/components/dashboard/CauseSelection";
import VeteranInitiative from "@/components/dashboard/VeteranInitiative";
import PricingPlans from "@/components/dashboard/PricingPlans";
import BatteryImpact from "@/components/dashboard/BatteryImpact";
import AnnualReport from "@/components/dashboard/AnnualReport";
import Sponsors from "@/components/dashboard/Sponsors";
import FoundingSection from "@/components/dashboard/FoundingSection";
import WelcomeExperience from "@/components/dashboard/WelcomeExperience";
import MembershipGear from "@/components/dashboard/MembershipGear";
import { api, endpoints } from "@/lib/api";
import { resolveUserId } from "@/lib/session";
import { DASHBOARD } from "@/constants/testIds";

const Dashboard = () => {
    const [state, setState] = useState({
        user: null,
        community: null,
        milestones: [],
        allocation: null,
        pricing: [],
        causes: [],
        selectedCause: "trees",
        batteryImpact: null,
        sponsors: [],
        report: null,
        loading: true,
        error: null,
    });

    useEffect(() => {
        let ignore = false;
        const uid = resolveUserId();
        const load = async () => {
            try {
                const [
                    user,
                    community,
                    milestones,
                    allocation,
                    pricing,
                    causes,
                    userCause,
                    batteryImpact,
                    sponsors,
                    report,
                ] = await Promise.all([
                    api.get(endpoints.userImpact(uid)),
                    api.get(endpoints.community()),
                    api.get(endpoints.milestones(uid)),
                    api.get(endpoints.allocation()),
                    api.get(endpoints.pricing()),
                    api.get(endpoints.causes()),
                    api.get(endpoints.userCause(uid)),
                    api.get(endpoints.batteryImpact()),
                    api.get(endpoints.sponsors()),
                    api.get(endpoints.annualReport(uid)),
                ]);
                if (ignore) return;
                setState({
                    user: user.data,
                    community: community.data,
                    milestones: milestones.data,
                    allocation: allocation.data,
                    pricing: pricing.data,
                    causes: causes.data,
                    selectedCause: userCause.data.selected_cause,
                    batteryImpact: batteryImpact.data,
                    sponsors: sponsors.data,
                    report: report.data,
                    loading: false,
                    error: null,
                });
            } catch (e) {
                if (!ignore)
                    setState((s) => ({ ...s, loading: false, error: "Unable to load dashboard." }));
            }
        };
        load();
        return () => {
            ignore = true;
        };
    }, []);

    const rates = state.community?.rates_per_sec;
    const initial = state.community
        ? Object.fromEntries(
              Object.entries(state.community).filter(
                  ([k]) => !["as_of", "rates_per_sec"].includes(k)
              )
          )
        : null;

    return (
        <div data-testid={DASHBOARD.root} className="min-h-screen bg-[#050505] text-white">
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#fff",
                    },
                }}
            />
            <TopNav user={state.user} />
            {state.user && <WelcomeExperience user={state.user} />}

            <main className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 sm:py-10 lg:px-10 lg:py-14">
                {state.loading && (
                    <div className="grid gap-6">
                        {[...Array(4)].map((_, i) => (
                            <div
                                key={i}
                                className="h-40 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]"
                            />
                        ))}
                    </div>
                )}
                {state.error && (
                    <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-6 text-sm text-red-200">
                        {state.error}
                    </div>
                )}
                {!state.loading && !state.error && (
                    <div className="flex flex-col gap-6 sm:gap-8">
                        <Sponsors sponsors={state.sponsors} />
                        <HeroImpact user={state.user} />
                        <FoundingSection user={state.user} />
                        <MembershipGear user={state.user} />
                        <CommunityTracker initial={initial} rates={rates} />

                        <MilestonesTracker milestones={state.milestones} />
                        <ImpactAllocation allocation={state.allocation} />

                        <CauseSelection
                            causes={state.causes}
                            selected={state.selectedCause}
                            onChange={(id) =>
                                setState((s) => ({ ...s, selectedCause: id }))
                            }
                        />

                        <VeteranInitiative />
                        <BatteryImpact data={state.batteryImpact} />
                        <PricingPlans plans={state.pricing} />
                        <AnnualReport report={state.report} />

                        <footer className="mt-4 flex flex-col items-center gap-2 border-t border-[#1f1f22] py-8 text-center">
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-600">
                                Powered by the EVIQ Community
                            </span>
                            <p className="max-w-2xl font-editorial text-lg italic text-zinc-400">
                                Every mile you drive with EVIQ helps build a more sustainable future.
                            </p>
                            <a
                                href="https://eviq.pro"
                                target="_top"
                                rel="noopener"
                                className="mt-4 inline-flex items-center gap-1.5 font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500 hover:text-[#a3e635]"
                            >
                                A subpage of eviq.pro
                                <span className="text-zinc-700">↗</span>
                            </a>
                        </footer>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Dashboard;
